import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - but don't redirect if already on login page
      // to prevent infinite redirect loop
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        // Use replace instead of href to avoid history stack issues
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authApi = {
  login: (email: string, password: string) => 
    api.post('/auth/login', { email, password }),
  
  getMe: () => 
    api.get('/auth/profile'),  // Backend uses /profile not /me
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  requestPasswordReset: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
  
  logout: () => 
    api.post('/auth/logout'),

  heartbeat: () =>
    api.post('/auth/heartbeat', {}),
};

// Triage API
export const triageApi = {
  getTasks: (params?: { status?: string; category?: string; modality?: string; page?: number; limit?: number }) =>
    api.get('/triage/tasks', { params }),
  
  getTask: (id: string | number) =>
    api.get(`/triage/tasks/${id}`),

  // Whether this message reads as a cancellation, and what the patient has
  // booked. Reports only — cancelling is a separate action.
  getCancellation: (id: string | number) =>
    api.get(`/triage/tasks/${id}/cancellation`),

  getAssignableUsers: () =>
    api.get('/triage/users'),
  
  updateTask: (id: string | number, data: Partial<any>) =>
    api.patch(`/triage/tasks/${id}`, data),

  assignTask: (id: string | number, assignedTo: string | number) =>
    api.patch(`/triage/tasks/${id}/assign`, { assignedTo }),
  
  addInternalNote: (id: string | number, note: string) =>
    api.post(`/triage/tasks/${id}/notes`, { text: note }),

  editInternalNote: (id: string | number, noteId: string, text: string) =>
    api.put(`/triage/tasks/${id}/notes/${noteId}`, { text }),

  deleteInternalNote: (id: string | number, noteId: string) =>
    api.delete(`/triage/tasks/${id}/notes/${noteId}`),

  // Only wellness recall tasks may be deleted; the API refuses anything
  // carrying a patient message, which is a record.
  deleteTask: (id: string | number) => api.delete(`/triage/tasks/${id}`),
  
  // `slots` rides along with the reply so the task records what was offered.
  // Omitted (not empty) means "leave the scheduling state alone" — axios drops
  // undefined keys, so the backend's "not an array → no bookkeeping" rule holds.
  sendResponse: (id: string | number, message: string, responseId?: string, slots?: any[]) =>
    api.post(`/triage/tasks/${id}/respond`, { message, responseId, slots }),

  uploadResponseAttachment: (taskId: string | number, responseId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('taskId', String(taskId));
    formData.append('responseId', responseId);
    return api.post('/response-attachments/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getSpruceAttachment: (messageId: string, attachmentId: string, conversationId?: string) =>
    api.get(`/spruce/attachment/${messageId}/${attachmentId}`, {
      params: conversationId ? { conversationId } : undefined,
    }),

  downloadSpruceAttachment: (messageId: string, attachmentId: string, conversationId?: string) =>
    api.get(`/spruce/attachment/${messageId}/${attachmentId}/download`, {
      params: conversationId ? { conversationId } : undefined,
      responseType: 'blob',
    }),
  
  createInternalTask: (data: {
    patientName: string;
    patientPhone: string;
    patientEmail?: string;
    internalPatientId?: string;
    contactId?: string;
    assignedTo?: string | number;
    category?: string;
    message: string;
  }) =>
    api.post('/triage/tasks/internal', data),

  updatePatientInfo: (id: string | number, data: {
    firstName?: string;
    lastName?: string;
    fullName?: string;
    patientPhone?: string;
    contactId?: string;
    spruceContactId?: string;
  }) =>
    api.post(`/triage/tasks/${id}/patient`, data),

  // Repoint a task at a different existing patient (identity only — the
  // rename flow above is updatePatientInfo).
  relinkPatient: (id: string | number, data: {
    name: string;
    internalId?: string | null;
    spruceContactId?: string | null;
    contactId?: string;
    phone?: string;
    email?: string;
    dateOfBirth?: string;
    gender?: string;
  }) =>
    api.patch(`/triage/tasks/${id}/patient-link`, data),

  resolveContactPhone: (id: string | number) =>
    api.get(`/triage/tasks/${id}/contact-phone`),

  initiateTask: (data: {
    patientName: string;
    patientPhone: string;
    patientEmail?: string;
    internalPatientId?: string;
    contactId?: string;
    assignedTo?: string | number;
    message: string;
  }) =>
    api.post('/triage/tasks/initiate', data),
};

// Whether the practice has enabled patient self-booking. The booking link is
// useless without it — the patient's page will not open — so the button that
// makes one is hidden when this is off.
export const aiSchedulingConfigApi = {
  get: () => api.get('/ai-scheduling-config'),
  update: (data: { enablePatientSelfBooking?: boolean }) =>
    api.put('/ai-scheduling-config', data),
};

// Patient booking links. The token comes back exactly once, at creation.
export const schedulingLinksApi = {
  create: (payload: {
    taskId?: string; patientName: string; patientPhone: string;
    internalPatientId?: string; serviceId: string; specialistId: string; ttlDays?: number;
    /**
     * Times to HOLD for this patient. Empty means an open-ended link. Each
     * may name its own service and provider — a lab draw and a doctor's
     * visit on one link; one that names neither takes the link's.
     */
    slots?: Array<{ date: string; time: string; durationMinutes?: number; serviceId?: string; specialistId?: string }>;
    purpose?: 'wellness';
  }) => api.post('/scheduling-links', payload),
  forTask: (taskId: string) => api.get(`/scheduling-links/task/${taskId}`),
  /** What holding these times would leave of each day. Writes nothing. */
  preview: (payload: { serviceId: string; specialistId: string; slots: Array<{ date: string; time: string; durationMinutes?: number }> }) =>
    api.post('/scheduling-links/preview', payload),
  /** Every live hold in a date window, for the calendar. */
  holds: (startDate: string, endDate: string) =>
    api.get('/scheduling-links/holds', { params: { startDate, endDate } }),
  revoke: (id: string) => api.post(`/scheduling-links/${id}/revoke`, {}),
  /**
   * Composes the note telling a patient their appointment is off, finds the
   * task to put it on, and mints a booking link when the practice allows
   * self-booking. Sends nothing.
   */
  cancellationNotice: (appointmentId: string, includeBookingLink: boolean) =>
    api.post(`/scheduling/appointments/${appointmentId}/cancellation-notice`, { includeBookingLink }),
  /** Moves a patient's self-booked appointment from tentative to scheduled. */
  // The empty object matters: a POST with no body sends no Content-Type, and a
  // middleware answers 400 "Content-Type must be application/json". Same reason
  // revoke above passes one.
  confirmBooking: (appointmentId: string) =>
    api.post(`/scheduling/appointments/${appointmentId}/confirm-booking`, {}),
};

export const aiSchedulingApi = {
  approveDraft: (payload: { taskId: string; message?: string; slots?: any[] }) =>
    api.post('/ai-scheduling/approve-draft', payload),
  saveDraft: (payload: { taskId: string; message: string; slots?: any[] }) =>
    api.post('/ai-scheduling/save-draft', payload),
  rejectDraft: (payload: { taskId: string }) =>
    api.post('/ai-scheduling/reject-draft', payload),
};

// Appointments API
export const appointmentsApi = {
  getAppointments: (params?: { startDate?: string; endDate?: string; provider?: string }) =>
    api.get('/appointments', { params }),
  
  getAppointment: (id: string) =>
    api.get(`/appointments/${id}`),
  
  createAppointment: (data: any) =>
    api.post('/appointments', data),
  
  updateAppointment: (id: string, data: any) =>
    api.put(`/appointments/${id}`, data),
  
  deleteAppointment: (id: string) =>
    api.delete(`/appointments/${id}`),
};

// Contacts API
export const contactsApi = {
  getContacts: (params?: { search?: string; status?: string; limit?: number; isArchived?: boolean }) =>
    api.get('/contacts', { params }),
  
  getContact: (id: string) =>
    api.get(`/contacts/${id}`),
  
  createContact: (data: any) =>
    api.post('/contacts', data),
  
  updateContact: (id: string, data: any) =>
    api.put(`/contacts/${id}`, data),
  
  searchContacts: (query: string) =>
    api.get('/contacts/search', { params: { q: query } }),

  mergeContacts: (primaryContactId: string, duplicateContactIds: string[], mergedBy?: string) =>
    api.post('/contacts/merge', { primaryContactId, duplicateContactIds, mergedBy }),
};

// Notifications API
export const notificationApi = {
  getNotifications: () =>
    api.get('/notification'),

  getUnreadCount: () =>
    api.get('/notification/unread-count'),

  markAsRead: (id: string | number) =>
    api.patch(`/notification/${id}/read`),

  markAllAsRead: () =>
    api.patch('/notification/mark-all-read'),

  getPushPublicKey: () =>
    api.get('/notification/push/public-key'),
  subscribePush: (subscription: PushSubscription | PushSubscriptionJSON, userAgent?: string) =>
    api.post('/notification/push/subscribe', { subscription, userAgent }),
  unsubscribePush: (endpoint: string) =>
    api.delete('/notification/push/subscribe', { data: { endpoint } }),
};

// Specialists API
export const specialistsApi = {
  getAll: () =>
    api.get('/specialists'),
  
  getActive: () =>
    api.get('/specialists/active'),
  
  getSpecialist: (id: string) =>
    api.get(`/specialists/${id}`),
  
  createSpecialist: (data: any) =>
    api.post('/specialists', data),
  
  updateSpecialist: (id: string, data: any) =>
    api.put(`/specialists/${id}`, data),
  
  deleteSpecialist: (id: string) =>
    api.delete(`/specialists/${id}`),
};

// Services API
export const servicesApi = {
  getAll: () =>
    api.get('/services'),
  
  getService: (id: string) =>
    api.get(`/services/${id}`),
  
  createService: (data: any) =>
    api.post('/services', data),
  
  updateService: (id: string, data: any) =>
    api.put(`/services/${id}`, data),
  
  deleteService: (id: string) =>
    api.delete(`/services/${id}`),
};

// Service-Specialist Mapping API
export const serviceMappingApi = {
  getAll: () =>
    api.get('/service-specialist-mapping'),
  
  getByService: (serviceId: string) =>
    api.get(`/service-specialist-mapping/service/${serviceId}`),
  
  getBySpecialist: (specialistId: string) =>
    api.get(`/service-specialist-mapping/specialist/${specialistId}`),
  
  create: (serviceId: string, specialistId: string, duration?: number | null) =>
    api.post('/service-specialist-mapping', { serviceId, specialistId, duration }),

  // Set or clear this provider's duration override for the service.
  // Pass null to clear it and fall back to the service's own duration.
  updateDuration: (id: string, duration: number | null) =>
    api.patch(`/service-specialist-mapping/${id}/duration`, { duration }),

  delete: (id: string) =>
    api.delete(`/service-specialist-mapping/${id}`),
};

// Scheduling API
export const schedulingApi = {
  getAvailability: (date: string) =>
    api.get(`/scheduling/availability/${date}`),
  
  // ignoreLinkId: show the times this link is holding as free. For the office
  // working the task that link belongs to — they are reserved FOR this patient,
  // so hiding them from the person offering them is backwards. Everyone else
  // still sees them as taken.
  getSlots: (serviceId: string, specialistId: string, date: string, ignoreLinkId?: string) =>
    api.get(`/scheduling/slots/${serviceId}/${specialistId}/${date}`,
      ignoreLinkId ? { params: { ignoreLinkId } } : undefined),

  // Is this exact time free? For a time the office types rather than picks
  // from the list — any minute. Answers { free, reason, duration }.
  checkTime: (serviceId: string, specialistId: string, date: string, time: string, ignoreLinkId?: string) =>
    api.get(`/scheduling/check-time/${serviceId}/${specialistId}/${date}/${time}`,
      ignoreLinkId ? { params: { ignoreLinkId } } : undefined),

  // Patients whose annual wellness exam falls due in the given month (YYYY-MM).
  getWellnessDue: (month: string) =>
    api.get('/scheduling/wellness-due', { params: { month } }),

  // Cancel one appointment. Sets its status and texts the patient when
  // this is the change that cancels it.
  // `taskId`: the task the office cancelled from, so the patient is texted
  // through its thread and it hears about the cancellation even when the
  // appointment was never linked to it.
  cancelAppointment: (id: string, taskId?: string) =>
    api.post(`/scheduling/appointments/${id}/cancel`, taskId ? { taskId } : {}),

  // A concrete time for every part of a multi-part appointment. Proposes only.
  getBundleProposal: (key: string, from: string) =>
    api.get(`/scheduling/bundles/${key}/proposal`, { params: { from } }),
  /** The rules behind the set buttons: steps, gap in days, same morning. */
  getBundles: () => api.get('/scheduling/bundles'),

  // Forget what was recorded about a patient's recall — the undo for adding
  // somebody by mistake, as distinct from saying they are not due.
  clearWellnessRecord: (patientId: string) =>
    api.delete(`/scheduling/wellness-due/${patientId}`),

  // Record what the office has done about a patient who is due a wellness exam.
  setWellnessStatus: (patientId: string, status: string, reason?: string) =>
    api.patch(`/scheduling/wellness-due/${patientId}`, { status, reason }),

  // Find a patient to put on the recall list by hand.
  searchWellnessPatients: (q: string) =>
    api.get('/scheduling/wellness-due/search', { params: { q } }),

  // Put a patient on the recall list for a month.
  addWellnessPatient: (patientId: string, month: string) =>
    api.post('/scheduling/wellness-due', { patientId, month }),

  // Plan which month a recall patient is contacted in. null forgets the plan.
  // Moving is not contacting: status is untouched.
  setWellnessMonth: (patientId: string, month: string | null) =>
    api.patch(`/scheduling/wellness-due/${patientId}/month`, { month }),

  // How many are already planned into each of the coming months, so a new
  // spread respects the last one.
  plannedCounts: (from: string, months: number) =>
    api.get('/scheduling/wellness-due/planned-counts', { params: { from, months } }),

  // Several moves at once - the applied half of a suggested spread.
  applyWellnessPlan: (assignments: Array<{ patientId: string; month: string }>) =>
    api.post('/scheduling/wellness-due/plan', { assignments }),

  // Create the scheduling task for a patient who is due, and mark them contacted.
  // The empty object matters: without a body axios sends no Content-Type and
  // the API rejects the request.
  startWellnessScheduling: (patientId: string) =>
    api.post(`/scheduling/wellness-due/${patientId}/task`, {}),
  
  // Who a phone number belongs to. Candidates, not a decision — phones are
  // shared, and the caller applies the rule about when it is safe to open one.
  contactForPhone: (phone: string) =>
    api.get('/scheduling/contact-for-phone', { params: { phone } }),

  searchPatients: (query: string) =>
    api.get('/scheduling/patients/search', { params: { q: query } }),
  
  createAppointment: (data: any) =>
    api.post('/scheduling/appointments', data),
  
  updateAppointment: (id: string, data: any) =>
    api.put(`/scheduling/appointments/${id}`, data),
  
  deleteAppointment: (id: string) =>
    api.delete(`/scheduling/appointments/${id}`),
  
  getAIRecommendations: (patientId: string, serviceId: string) =>
    api.post('/scheduling/ai-recommendations', { patientId, serviceId }),
  
  quickSchedule: (data: any) =>
    api.post('/scheduling/quick-schedule', data),
};

// Fax signature queue — Admin allowlist + triage Signatures drawer
export const faxSignaturesApi = {
  get: () => api.get('/fax-signatures'),
  update: (payload: {
    enabled?: boolean;
    phones?: string[];
    autoSendPhones?: string[];
    autoSendEnabled?: boolean;
    alsoMatchTextCues?: boolean;
    suggestedPhones?: string[];
  }) => api.put('/fax-signatures', payload),
  queue: () => api.get('/fax-signatures/queue'),
  classify: (payload: { senderPhone?: string; text?: string; isFax?: boolean }) =>
    api.post('/fax-signatures/classify', payload),
  setLane: (id: string | number, lane: 'signature' | 'clinical' | 'filing') =>
    api.patch(`/fax-signatures/tasks/${id}/lane`, { lane }),
  uploadPracticeSignature: (file: File) => {
    const fd = new FormData();
    fd.append('signature', file);
    return api.post('/fax-signatures/practice-signature', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getViewer: (id: string | number) =>
    api.get(`/fax-signatures/tasks/${id}/viewer`, { timeout: 120000 }),
  prepareSign: (id: string | number, payload: any) =>
    api.post(`/fax-signatures/tasks/${id}/prepare-sign`, payload, { timeout: 120000 }),
  signAndReturn: (id: string | number, payload: any) =>
    api.post(`/fax-signatures/tasks/${id}/sign-and-return`, payload, { timeout: 120000 }),
  acceptSuggestion: (phone: string) =>
    api.post('/fax-signatures/suggestions/accept', { phone }),
};

// File-only inbound faxes — hold queue + just-filed list (per-login rotation)
export const faxFilingsApi = {
  queue: () => api.get('/fax-filings/queue'),
  getPrefs: () => api.get('/fax-filings/prefs'),
  updatePrefs: (rotationDays: number) =>
    api.put('/fax-filings/prefs', { rotationDays }),
  searchPatients: (q: string) =>
    api.get('/fax-filings/search-patients', { params: { q } }),
  fileToPatient: (id: string, internalId: string) =>
    api.post(`/fax-filings/${id}/file`, { internalId }),
  preview: (id: string) => api.get(`/fax-filings/${id}/preview`, { timeout: 120000 }),
  split: (id: string) => api.post(`/fax-filings/${id}/split`),
};

// Proxy SMS intake — Admin allowlist + first-line patient name → triage card
export const proxyIntakeApi = {
  get: () => api.get('/proxy-intake'),
  update: (payload: {
    enabled?: boolean;
    phones?: string[];
    spruceContactIds?: string[];
    label?: string;
  }) => api.put('/proxy-intake', payload),
  preview: (payload: { text: string; senderPhone?: string; patientId?: string }) =>
    api.post('/proxy-intake/preview', payload),
};

// AI Skills API — compression / skill-store knobs (meds, messages, spruce).
export const aiSkillsApi = {
  list: () => api.get('/ai-skills'),
  get: (key: string) => api.get(`/ai-skills/${key}`),
  // Submits the change for review; the approved version stays live until it is approved.
  update: (key: string, patch: { aggressiveness?: string; enabled?: boolean }) =>
    api.put(`/ai-skills/${key}`, patch),
  proposals: () => api.get('/ai-skills/proposals'),
  previewMeds: (spruceEntityId: string, aggressiveness?: string) =>
    api.get(`/ai-skills/preview/meds/${spruceEntityId}`, { params: aggressiveness ? { aggressiveness } : {} }),
  previewMessages: (items: any[], aggressiveness?: string) =>
    api.post('/ai-skills/preview/messages', { items, aggressiveness }),
};

// AI Voice API — practice-wide tunable voice for AI-drafted messages.
const voicemailPath = (messageId: string, attachmentId: string) =>
  `/voicemail/${encodeURIComponent(messageId)}/${encodeURIComponent(attachmentId)}`;

export const voicemailApi = {
  audio: (messageId: string, attachmentId: string) =>
    api.get(`${voicemailPath(messageId, attachmentId)}/audio`, { responseType: 'blob', timeout: 120000 }),
  get: (messageId: string, attachmentId: string) => api.get(voicemailPath(messageId, attachmentId)),
  transcribe: (messageId: string, attachmentId: string) =>
    api.post(`${voicemailPath(messageId, attachmentId)}/transcribe`, {}, { timeout: 300000 }),
};

export const aiVoiceApi = {
  get: () => api.get('/ai-voice'),
  update: (payload: {
    preset?: string;
    customText?: string;
    default?: { preset: string; customText?: string };
    modules?: Record<string, { preset: string; customText?: string } | null>;
    library?: {
      concise_warm?: string;
      brief_clinical?: string;
      detailed?: string;
      custom?: string;
    };
  }) => api.put('/ai-voice', payload),
};

// Settings API
export const settingsApi = {
  getAll: (category?: string) =>
    api.get('/settings', { params: category ? { category } : {} }),
  
  get: (key: string) =>
    api.get(`/settings/${key}`),
  
  update: (key: string, value: any) =>
    api.put(`/settings/${key}`, { value }),
  
  bulkUpdate: (settings: Record<string, any>) =>
    api.put('/settings', { settings }),
  
  getSmsTemplates: () =>
    api.get('/settings/templates/sms'),
  
  reset: () =>
    api.post('/settings/reset'),
};

// Contact Sync API
export const contactSyncApi = {
  getStatus: () =>
    api.get('/contact-sync/status'),
  
  levitanmdPreview: () =>
    api.post('/contact-sync/levitanmd/preview'),

  levitanmdArchive: (contactIds: string[]) =>
    api.post('/contact-sync/levitanmd/archive-detagged', { contactIds }),
  
  start: (intervalMs?: number) =>
    api.post('/contact-sync/start', { intervalMs }),
  
  stop: () =>
    api.post('/contact-sync/stop'),
};

// Bulk Messaging API
export const bulkMessagingApi = {
  send: (contactIds: string[], message: string, campaignName?: string) =>
    api.post('/bulk-messaging/send', { contactIds, message, campaignName }),
  
  getCampaigns: (page?: number, limit?: number) =>
    api.get('/bulk-messaging/campaigns', { params: { page, limit } }),
  
  getCampaign: (id: string) =>
    api.get(`/bulk-messaging/campaigns/${id}`),
};

// Profile API
export const profileApi = {
  getProfile: () =>
    api.get('/profile'),
  
  updateProfile: (data: { firstName?: string; lastName?: string; name?: string; phone?: string; avatarUrl?: string }) =>
    api.put('/profile', data),
  
  getNotificationPreferences: () =>
    api.get('/profile/notifications'),
  
  updateNotificationPreferences: (data: {
    notifySms?: boolean;
    notifyEmail?: boolean;
    notifyInApp?: boolean;
    notifyUrgentOnly?: boolean;
    notifyPush?: boolean;
    notifyUrgencyLevels?: string[];
    quietHoursStart?: string;
    quietHoursEnd?: string;
    dndWindows?: Array<{ days: string[]; start: string; end: string }>;
    timezone?: string | null;
    dateTimeFormat?: string | null;
    tonePreference?: string | null;
  }) =>
    api.put('/profile/notifications', data),
};

// Two-Factor Authentication API
export const twoFactorApi = {
  getStatus: () =>
    api.get('/2fa/status'),
  
  enable: (method: 'sms' | 'email') =>
    api.post('/2fa/enable', { method }),
  
  verify: (code: string) =>
    api.post('/2fa/verify', { code }),
  
  disable: (password: string) =>
    api.post('/2fa/disable', { password }),
  
  requestCode: (userId: number, method?: 'sms' | 'email') =>
    api.post('/2fa/request-code', { userId, method }),
  
  verifyLogin: (userId: number, code: string) =>
    api.post('/2fa/verify-login', { userId, code }),
  
  verifyBackup: (userId: number, backupCode: string) =>
    api.post('/2fa/verify-backup', { userId, backupCode }),
};

// Branding API (public - no auth needed for get)
export const brandingApi = {
  getBranding: () =>
    api.get('/branding'),
  
  updateBranding: (data: { headerText?: string; sloganText?: string; copyrightText?: string }) =>
    api.put('/branding', data),
  
  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post('/branding/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  removeLogo: () =>
    api.delete('/branding/logo'),
};

// AI Training API
export const aiTrainingApi = {
  // Clinic knowledge goes to the skills review queue as a draft.
  proposeKnowledge: (text: string, fileName?: string) =>
    api.post('/ai-training/propose-knowledge', { text, fileName }),
  
  getStats: (days?: number) =>
    api.get('/ai-training/stats', { params: { days } }),
  
  getRecent: (limit?: number) =>
    api.get('/ai-training/recent', { params: { limit } }),
};

// Patient Details API
// Structured clinical coding + diagnoses (problem list / PT DX).
export interface CodeOption {
  code: string;
  description: string;
  system: string;
  billable?: boolean;
}
export interface PatientDiagnosis {
  id: string;
  description: string;
  icd10_code?: string | null;
  icd9_code?: string | null;
  snomed_code?: string | null;
  acuity?: string | null;
  status: string; // active | chronic | resolved
  onset_date?: string | null;
  stop_date?: string | null;
  comment?: string | null;
  source?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}
// One structured item in a patient_details list (e.g. a single medication
// or a single allergy). Rendered as an editable chip in the list manager.
export interface PatientDetailItem {
  id: string;
  category: string;
  content: string;
  source?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}
// A single visit note (read-only) from patient_encounters; PF rich-text is
// cleaned to plain text server-side.
export interface PatientEncounter {
  id: string;
  encounter_date?: string | null;
  encounter_type?: string | null;
  chief_complaint?: string | null;
  provider?: string | null;
  chart_note_type?: string | null;
  is_virtual?: boolean | null;
  subjective?: string | null;
  objective?: string | null;
  assessment?: string | null;
  plan?: string | null;
  snapshot_diagnosis?: string | null;
  snapshot_medications?: string | null;
  source?: string | null;
}
export const clinicalApi = {
  searchCodes: (q: string, system: string = 'icd10') =>
    api.get(`/clinical/codes/search`, { params: { q, system } }),
  listEncounters: (spruceEntityId: string) =>
    api.get(`/clinical/patient/${spruceEntityId}/encounters`),
  listDiagnoses: (spruceEntityId: string, status: 'all' | 'active' = 'all') =>
    api.get(`/clinical/patient/${spruceEntityId}/diagnoses`, { params: { status } }),
  addDiagnosis: (
    spruceEntityId: string,
    payload: { description: string; icd10_code?: string | null; snomed_code?: string | null; acuity?: string | null; status?: string; comment?: string | null },
  ) => api.post(`/clinical/patient/${spruceEntityId}/diagnoses`, payload),
  updateDiagnosis: (id: string, payload: Record<string, unknown>) =>
    api.patch(`/clinical/diagnoses/${id}`, payload),
  deleteDiagnosis: (id: string) => api.delete(`/clinical/diagnoses/${id}`),

  // Structured detail lists (current meds, allergies, …) — one row per item.
  listDetails: (spruceEntityId: string, category: string) =>
    api.get(`/clinical/patient/${spruceEntityId}/details`, { params: { category } }),
  addDetailItem: (spruceEntityId: string, category: string, content: string) =>
    api.post(`/clinical/patient/${spruceEntityId}/details`, { category, content }),
  updateDetailItem: (id: string, content: string) =>
    api.patch(`/clinical/details/${id}`, { content }),
  deleteDetailItem: (id: string) => api.delete(`/clinical/details/${id}`),
};

export const patientDetailsApi = {
  getPatient: (spruceEntityId: string) =>
    api.get(`/patient-details/patient/${spruceEntityId}`),

  // Pulls enriched profile fields (MRN, insurance, address, emergency contact,
  // fee/payment status, etc.) from the external `levitanai_contacts` DB.
  // Resolves to { external: null } when the external DB is unreachable so
  // callers can quietly fall back to the un-enriched view.
  getEnriched: (spruceEntityId: string) =>
    api.get(`/patient-details/patient/${spruceEntityId}/enriched`),

  getDocuments: (spruceEntityId: string) =>
    api.get(`/patient-details/patient/${spruceEntityId}/documents`),

  getSummary: (spruceEntityId: string) =>
    api.get(`/patient-details/patient/${spruceEntityId}/summary`),

  getHistory: (
    spruceEntityId: string,
    opts?: { includeConsultations?: boolean },
  ) =>
    api.get(`/patient-details/patient/${spruceEntityId}/history`, {
      params: opts?.includeConsultations ? { includeConsultations: 1 } : undefined,
    }),

  addDetail: (spruceEntityId: string, category: string, content: string, createdBy?: string) =>
    api.post(`/patient-details/patient/${spruceEntityId}/details`, { category, content, createdBy }),

  uploadDocument: (
    spruceEntityId: string,
    file: File,
    meta?: {
      patientName?: string;
      patientPhone?: string;
      patientEmail?: string;
      dateOfBirth?: string;
      gender?: string;
      documentDate?: string;
    }
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.patientName) formData.append('patientName', meta.patientName);
    if (meta?.patientPhone) formData.append('patientPhone', meta.patientPhone);
    if (meta?.patientEmail) formData.append('patientEmail', meta.patientEmail);
    if (meta?.dateOfBirth) formData.append('dateOfBirth', meta.dateOfBirth);
    if (meta?.gender) formData.append('gender', meta.gender);
    if (meta?.documentDate) formData.append('documentDate', meta.documentDate);
    return api.post(`/patient-details/patient/${spruceEntityId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadText: (
    spruceEntityId: string,
    payload: {
      text: string;
      title?: string;
      createdBy?: string;
      documentDate?: string;
      patientName?: string;
      patientPhone?: string;
      patientEmail?: string;
      dateOfBirth?: string;
      gender?: string;
    }
  ) =>
    api.post(`/patient-details/patient/${spruceEntityId}/documents/text`, payload),

  downloadDocument: (spruceEntityId: string, docId: string, inline = false) =>
    api.get(`/patient-details/patient/${spruceEntityId}/documents/${docId}/download`, {
      responseType: 'blob',
      params: inline ? { inline: '1' } : {},
    }),

  summarizeDocument: (spruceEntityId: string, docId: string, force = false) =>
    api.post(`/patient-details/patient/${spruceEntityId}/documents/${docId}/summarize${force ? '?force=1' : ''}`),

  summarizeAllDocuments: (spruceEntityId: string) =>
    api.post(`/patient-details/patient/${spruceEntityId}/documents/summarize-all`),

  listConsultations: (spruceEntityId: string) =>
    api.get(`/patient-details/patient/${spruceEntityId}/consultations`),

  createConsultation: (spruceEntityId: string, payload?: { title?: string; createdBy?: string; patientName?: string; patientPhone?: string; patientEmail?: string; dateOfBirth?: string; gender?: string }) =>
    api.post(`/patient-details/patient/${spruceEntityId}/consultations`, payload || {}),

  renameConsultation: (threadId: string, title: string) =>
    api.patch(`/patient-details/consultations/${threadId}`, { title }),

  getConsultationMessages: (threadId: string) =>
    api.get(`/patient-details/consultations/${threadId}/messages`),

  sendConsultationMessage: (threadId: string, payload: { content: string }) =>
    api.post(`/patient-details/consultations/${threadId}/messages`, payload),
};

// Unfiled Labs API
export const unfiledLabsApi = {
  list: () =>
    api.get('/unfiled-labs'),

  get: (id: string) =>
    api.get(`/unfiled-labs/${id}`),

  attach: (id: string, payload: { contactId: string; summarize?: boolean }) =>
    api.post(`/unfiled-labs/${id}/attach`, payload),

  split: (id: string, payload: { splits: Array<{ startPage: number; endPage: number }> }) =>
    api.post(`/unfiled-labs/${id}/split`, payload),
};

// Lab Directory API
export const labDirectoryApi = {
  list: (includeInactive?: boolean) =>
    api.get('/labs', { params: includeInactive !== undefined ? { includeInactive } : undefined }),
  create: (payload: any) =>
    api.post('/labs', payload),
  update: (id: string, payload: any) =>
    api.patch(`/labs/${id}`, payload),
  deactivate: (id: string) =>
    api.delete(`/labs/${id}`),
};

// DEV ONLY: Admin > Approved Outbound SMS. The route does not exist on prod, so the tab stays hidden there.
export interface OutboundAllowlistEntry {
  number: string;
  label: string;
  addedAt: string | null;
  addedBy: string | null;
}

export interface OutboundAllowlistState {
  devGuard: boolean;
  enabled: boolean;
  source?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
  numbers: OutboundAllowlistEntry[];
  loadError?: string;
}

export const outboundAllowlistApi = {
  get: () => api.get<OutboundAllowlistState>('/admin/outbound-allowlist'),
  add: (number: string, label?: string) =>
    api.post<OutboundAllowlistState>('/admin/outbound-allowlist', { number, label }),
  remove: (number: string) =>
    api.delete<OutboundAllowlistState>(`/admin/outbound-allowlist/${encodeURIComponent(number)}`),
};

// Draft rating (dev only): blind rating of two reply drafts per patient message
export interface DraftRatingCase {
  n: number;
  date: string;
  message: string;
  A: string;
  B: string;
}

export interface DraftRatingSheet {
  id: string;
  label: string;
  total: number;
  finishedAt: string | null;
}

export interface DraftRatingState {
  success: boolean;
  sheet: string;
  intro: string;
  sheets: DraftRatingSheet[];
  cases: DraftRatingCase[];
  answers: Record<string, string>;
  finishedAt: string | null;
  canSeeResults: boolean;
}

export interface DraftRatingTally {
  rater: string;
  savedAt: string | null;
  finishedAt: string | null;
  total: number;
  answered: number;
  preferred: { ours: number; other: number; tie: number };
  ours: Record<string, number>;
  other: Record<string, number>;
}

export interface DraftRatingResults {
  success: boolean;
  labels: { ours: string; other: string };
  raters: DraftRatingTally[];
}

export interface AiRatingSkill {
  id: number;
  key: string;
  version: number;
  kind: string;
  feature: string;
  name: string;
  description: string | null;
  text: string;
  status: 'draft' | 'approved' | 'rejected' | 'retired';
  source: string;
  author: string | null;
  createdAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  warnings: string[];
  textLocked: boolean;
  /** Staff-edited replies from Ask Levitan AI: the AI draft they started from. */
  aiDraft?: string;
}

export interface AiRatingReview {
  success: boolean;
  topicDrafts: AiRatingSkill[];
  practice: AiRatingSkill[];
  examples: Record<string, { version: number; reviewedBy: string | null; reviewedAt: string | null }>;
  editCandidates?: AiRatingSkill[];
}

export const draftRatingApi = {
  get: (sheet = '1') => api.get<DraftRatingState>('/admin/draft-rating', { params: { sheet } }),
  save: (sheet: string, answers: Record<string, string>, finished = false) =>
    api.put<{ success: boolean; savedAt: string; finishedAt: string | null }>('/admin/draft-rating/answers', { answers, finished }, { params: { sheet } }),
  results: (sheet: string) => api.get<DraftRatingResults>('/admin/draft-rating/results', { params: { sheet } }),
  review: () => api.get<AiRatingReview>('/admin/draft-rating/review'),
  reviewAction: (id: number, action: 'save' | 'approve' | 'reject', text?: string) =>
    api.post<{ success: boolean; unchanged?: boolean; warning?: string }>(`/admin/draft-rating/review/${id}`, { action, text }, { timeout: 90000 }),
  approveCase: (sheet: string, n: number, text: string) =>
    api.post<{ success: boolean; warnings?: string[]; removed?: string[] }>('/admin/draft-rating/cases/approve', { n, text }, { params: { sheet }, timeout: 90000 }),
};

// Zaprite API
export const zapriteApi = {
  getLink: (contactId: string) =>
    api.get(`/zaprite/contacts/${contactId}/link`),

  saveLink: (contactId: string, payload: { externalCustomerId: string; linkedBy?: string; externalMetadata?: any }) =>
    api.post(`/zaprite/contacts/${contactId}/link`, payload),

  getInvoices: (contactId: string) =>
    api.get(`/zaprite/contacts/${contactId}/invoices`),

  getBillingDetails: (contactId: string) =>
    api.get(`/zaprite/contacts/${contactId}/billing-details`),

  sendInvoice: (contactId: string, orderId: string, payload?: { message?: string }) =>
    api.post(`/zaprite/contacts/${contactId}/invoices/${orderId}/send`, payload),

  syncAll: () =>
    api.post('/zaprite/sync-all', {}),
};

export const practiceFusionApi = {
  lookup: (params: { name?: string; email?: string; phone?: string }) =>
    api.get("/practice-fusion/lookup", { params }),
};
export const activityApi = {
  record: (events: Array<Record<string, unknown>>) => api.post('/activity/events', { events }),
};
